import { Alert, InlineAlert, MailTo } from '@quizzach/styles';
import { IcAlertRound, IcAlertTriangle, IcInfo } from '@quizzach/assets';
import { useTranslation } from 'react-i18next';

const EMAIL = 'team@quizzach.com';

// icons draw with currentColor; the alert surfaces set the severity color
const severityIcon = {
	error: IcAlertRound,
	warning: IcAlertTriangle,
	info: IcInfo,
} as const;

const useAlertMessages = (): Record<string, React.ReactNode> => {
	const { t } = useTranslation();

	return {
		default: (
			<>
				{t('error.default')}{' '}
				<MailTo href={`mailto:${EMAIL}`} $clean>
					{EMAIL}
				</MailTo>
			</>
		),

		customizatorSelections: t('error.customizatorSelections'),
		addToCart: (
			<>
				{t('error.addToCart')}{' '}
				<MailTo href={`mailto:${EMAIL}`} $clean>
					{EMAIL}
				</MailTo>
			</>
		),
		addToCartShort: t('error.addToCartShort'),
		conflict: t('error.conflict'),
		noCategorySelected: t('error.noCategorySelected'),
		noQuickQuestions: t('error.noQuickQuestions'),
		noCategoryRetroactive: t('error.noCategoryRetroactive'),
		maxExceeded: t('error.maxExceeded'),
		minExceeded: t('error.minExceeded'),
		customizer: t('error.customizer'),
		cartFetch: t('error.cartFetch'),
		cartEmpty: t('error.cartEmpty'),
		notValidAuth: t('error.notValidAuth'),
		termsConsent: t('error.termsConsent'),
		emailInUse: t('error.emailInUse'),
	};
};

interface MessageProps {
	code: string;
	className?: string;
	style?: React.CSSProperties;
	$fixed?: boolean;
	type: 'error' | 'warning' | 'info';
}

export const AlertMessage = ({
	code,
	className,
	style,
	$fixed,
	type,
}: MessageProps) => {
	const message = useAlertMessages();
	const Icon = severityIcon[type] ?? IcAlertRound;

	return (
		<Alert className={className} style={style} $fixed={$fixed} type={type}>
			<div className='alert-icon'>
				<Icon size={22} />
			</div>
			<div>
				<div className='alert-text'>{message[code]}</div>
			</div>
		</Alert>
	);
};

interface InlineMessageProps {
	message?: string;
	className?: string;
	style?: React.CSSProperties;
	$fixed?: boolean;
	type: 'error' | 'warning' | 'info';
}

export const InlineAlertMessage = ({
	message,
	className,
	style,
	type,
	$fixed,
}: InlineMessageProps) => {
	const Icon = severityIcon[type] ?? IcAlertRound;

	return (
		<InlineAlert
			className={className}
			style={style}
			type={type}
			$fixed={$fixed}
		>
			<div className='alert-icon'>
				<Icon size={17} />
			</div>
			{message && (
				<div>
					<div className='alert-text'>{message}</div>
				</div>
			)}
		</InlineAlert>
	);
};
